import { ExpandMoreOutlined } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { generateClient } from "aws-amplify/api";
import { fetchUserAttributes } from "aws-amplify/auth";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from '../../contexts/UserContext';
import { createContactFieldMeta, updateContactFieldMeta } from "../../graphql/mutations";
import { cardStyle } from "../../theme";
import { ConfirmDialog } from "../confirmDialog/confirmDialog";
import CallWindowEditor from "./callWindowEditor";

const booleanTypes = {
    Unspecified: "0",
    True: "1",
    False: "2"
};

const vanillaMetadata = {
    cell: booleanTypes.Unspecified,
    consent: booleanTypes.Unspecified,
    writtenConsent: '',
    optOut: booleanTypes.Unspecified,
    preferred: booleanTypes.Unspecified,
    thirdParty: booleanTypes.Unspecified,
    callWindows: [],
    dontCallOnWeekends: booleanTypes.Unspecified
};

const booleanFields = ["cell", "consent", "optOut", "preferred", "thirdParty", "dontCallOnWeekends"];

export default function MetadataDialog(props) {
    const client = generateClient();
    const userContext = useContext(UserContext);

    const [metadata, setMetadata] = useState(vanillaMetadata);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [metadataAccordionExpanded, setMetadataAccordionExpanded] = useState(true);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

    const metadataId = useRef(undefined);

    const fieldsByName = useMemo(() => (userContext?.customFields?.length > 0 ? userContext.customFields : []).reduce((acc, cur) => Object.assign(acc, { [cur.name]: cur }), {
        phone: { displayName: "Phone" },
        cell: { displayName: "Cell" },
        email: { displayName: "Email" }
    }), [userContext]);

    useEffect(() => {
        async function getData() {
            setLoading(true);
            try {
                const fieldMetaData = props?.metadata?.find(meta => meta.field === props.field);
                if (!fieldMetaData) {
                    setMetadata({ ...vanillaMetadata });
                    metadataId.current = undefined;
                    setLoading(false);
                    return;
                }
                setMetadata(massageData(fieldMetaData?.poc));
                metadataId.current = fieldMetaData?.id;

            } catch (err) {
                console.error(err);
            }

            setLoading(false);
        }

        if (props.open) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    /**
     * Converts a DynamoDB poc metadata object into the type that this form can use.
     * @param {any} poc The metadata.
     * @returns The an updated object.
     */
    function massageData(poc) {
        const newObject = {};

        // Get us a vanilla metadata object.
        Object.assign(newObject, { ...vanillaMetadata });

        // Overwrite any existing metadata from the parameter into the metadata object (which is vanilla at this point).
        Object.assign(newObject, poc ?? {});

        // Nuke the typename for any nested items (the screenpop appsync client tends to put them in).
        delete newObject.__typename;
        for (const window of newObject.callWindows ?? []) {
            delete window?.__typename;
            delete window?.tmz?.__typename;
            delete window?.beginTime?.__typename;
            delete window?.endTime?.__typename;
        }

        // Normalize everything for the form (use the enum booleanTypes instead of true, false, and null).
        for (const booleanField of booleanFields) {
            if (newObject[booleanField] === true) {
                newObject[booleanField] = booleanTypes.True;
            } else if (newObject[booleanField] === false) {
                newObject[booleanField] = booleanTypes.False;
            } else if (newObject[booleanField] == null) {
                newObject[booleanField] = booleanTypes.Unspecified;
            }
        }

        newObject.writtenConsent = moment.utc(newObject.writtenConsent);
        if (newObject.writtenConsent.isValid()) {
            newObject.writtenConsent = newObject.writtenConsent.format("YYYY-MM-DD");
        } else {
            newObject.writtenConsent = '';
        }

        return newObject;
    }

    function suppressKeyDown(e) {
        e.preventDefault();
    }

    function handleClose(dirty) {
        if (dirty) {
            setConfirmCloseOpen(true);
        } else {
            props.close();
        }
    }

    return (
        <>
            <Dialog open={props.open} maxWidth="md">
                <DialogTitle>
                    <Typography variant="h4">{fieldsByName[props.field]?.displayName} Metadata</Typography>
                </DialogTitle>
                <DialogContent style={{ paddingBottom: "20px" }}>
                    {loading &&
                        <CircularProgress variant='indeterminate' />}
                    {!loading &&
                        <>
                            <Formik
                                initialValues={metadata}
                                enableReinitialize
                                onSubmit={async values => {
                                    setSaving(true);

                                    const body = {
                                        tenant: props.tenant,
                                        field: props.field,
                                        contact: props.contactId,
                                        type: "poc", // There might be more fields in the future
                                        poc: {
                                            callWindows: values.callWindows,
                                        },
                                        updatedBy: (await fetchUserAttributes()).email
                                    };

                                    const writtenConsentDate = new Date(values.writtenConsent);
                                    if (!isNaN(writtenConsentDate)) {
                                        body.poc.writtenConsent = writtenConsentDate.toISOString().split('T')[0];
                                    }

                                    for (const booleanField of booleanFields) {
                                        switch (values[booleanField]) {
                                            case booleanTypes.True:
                                                body.poc[booleanField] = true;
                                                break;
                                            case booleanTypes.False:
                                                body.poc[booleanField] = false;
                                                break;
                                            default:
                                                break;
                                        }
                                    }

                                    try {
                                        if (!metadataId.current) {
                                            const result = await client.graphql({
                                                query: createContactFieldMeta,
                                                variables: {
                                                    input: body
                                                }
                                            });
                                            metadataId.current = result.data.createContactFieldMeta.id;
                                            enqueueSnackbar("Contact Metadata Created", { variant: "success" });
                                        } else {
                                            body.id = metadataId.current;
                                            await client.graphql({
                                                query: updateContactFieldMeta,
                                                variables: {
                                                    input: body
                                                }
                                            });
                                        }
                                    } catch (err) {
                                        console.error(err);
                                        enqueueSnackbar("Something went wrong with updating metadata", {
                                            variant: "error",
                                        });
                                    }

                                    setMetadata({ ...massageData(body.poc) });

                                    setSaving(false);
                                }}
                            >
                                {formikProps => (
                                    <form onSubmit={formikProps.handleSubmit}>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <Accordion variant='outlined' expanded={metadataAccordionExpanded}>
                                                <AccordionSummary expandIcon={<ExpandMoreOutlined color='primary' />} onClick={() => setMetadataAccordionExpanded(!metadataAccordionExpanded)}>
                                                    Metadata
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                                        <BooleanSelect onChange={formikProps.handleChange} value={formikProps.values.cell ?? booleanTypes.Unspecified} field='cell' label='Is this a cell phone number?' />
                                                        <BooleanSelect onChange={formikProps.handleChange} value={formikProps.values.consent ?? booleanTypes.Unspecified} field='consent' label='Received consent from the contact' />

                                                        <BooleanSelect onChange={formikProps.handleChange} value={formikProps.values.optOut ?? booleanTypes.Unspecified} field='optOut' label='Has the contact opted out?' />

                                                        <BooleanSelect onChange={formikProps.handleChange} value={formikProps.values.preferred ?? booleanTypes.Unspecified} field='preferred' label='Is this the preferred form of communication?' />

                                                        <BooleanSelect onChange={formikProps.handleChange} value={formikProps.values.thirdParty ?? booleanTypes.Unspecified} field='thirdParty' label='Third party' />
                                                        <BooleanSelect onChange={formikProps.handleChange} value={formikProps.values.dontCallOnWeekends ?? booleanTypes.Unspecified} field='dontCallOnWeekends' label="Don't call on weekends" />
                                                        <TextField
                                                            label="Date of written consent"
                                                            type="date"
                                                            variant="outlined"
                                                            name="writtenConsent"
                                                            onKeyDown={suppressKeyDown}
                                                            InputLabelProps={{ shrink: true }}
                                                            value={formikProps.values.writtenConsent}
                                                            onChange={formikProps.handleChange}
                                                        />
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                            <Accordion variant='outlined' expanded={!metadataAccordionExpanded}>
                                                <AccordionSummary expandIcon={<ExpandMoreOutlined color='primary' />} onClick={() => setMetadataAccordionExpanded(!metadataAccordionExpanded)}>
                                                    Call Windows
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box display="flex" flexDirection="column" gap={2}>
                                                    <Card variant="outlined" sx={cardStyle}>
                                                        <CardContent>
                                                            {formikProps.values.callWindows.length === 0 &&
                                                                <Typography variant="body2" color="textSecondary">
                                                                    No call windows defined.
                                                                </Typography>
                                                            }
                                                            {formikProps.values.callWindows.length > 0 &&
                                                                <TableContainer>
                                                                    <Table>
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Days</TableCell>
                                                                                <TableCell>Begin Time</TableCell>
                                                                                <TableCell>End Time</TableCell>
                                                                                <TableCell>Time Zone</TableCell>
                                                                                <TableCell>DST</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {formikProps.values.callWindows.map((window, index) => (
                                                                                <TableRow key={index}>
                                                                                    <TableCell>
                                                                                        {window.days.map(day => moment().day(day).format('dddd')).join(', ')}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {window?.beginTime?.hour.toString().padStart(2, '0')}:
                                                                                        {window?.beginTime?.minute.toString().padStart(2, '0')}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {window?.endTime?.hour.toString().padStart(2, '0')}:
                                                                                        {window?.endTime?.minute.toString().padStart(2, '0')}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {window?.tmz ? window?.tmz?.offset >= 0 ? `UTC+${window?.tmz?.offset}` : `UTC${window?.tmz?.offset}` : '-'}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {window?.tmz ? window?.tmz?.dst ? 'Yes' : 'No' : '-'}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            }
                                                        </CardContent>
                                                    </Card>
                                                    <CallWindowEditor callWindows={formikProps.values.callWindows} onChange={formikProps.handleChange} />
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                            <Box display="flex" flexDirection="row" gap={2} justifyContent="flex-end">
                                                <Button color="primary" variant="outlined" onClick={() => handleClose(formikProps.dirty)}>
                                                    Close
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    type="submit"
                                                    disabled={saving || !formikProps.dirty}
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        </Box>
                                    </form>
                                )}
                            </Formik>
                        </>}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={confirmCloseOpen}
                actionTwoHandler={() => setConfirmCloseOpen(false)}
                actionOneHandler={props.close}
                title="Discard Changes?"
                description="You have unsaved changes. Are you sure you want to discard them?"
                actionOneText="Discard"
                actionTwoText="Cancel"
            />
        </>
    );
}

const BooleanSelect = memo((props) => {
    const { onChange, label, field, value } = props;

    return (
        <FormControl>
            <InputLabel>{label}</InputLabel>
            <Select name={field} onChange={onChange} value={value} label={label}>
                {Object.keys(booleanTypes).map(type =>
                    <MenuItem key={type} value={booleanTypes[type]}>{type}</MenuItem>)}
            </Select>
        </FormControl>
    );
}, (oldProps, newProps) => oldProps.label === newProps.label && oldProps.field === newProps.field && oldProps.value === newProps.value);